import * as signalR from '@microsoft/signalr';
import { API_BASE, TIFOXR_TOKEN_KEY } from '../api/apiConfig';
import { getSpaceId } from '../api/client';
import { SIGNALR_RESYNC_EVENT } from '../constants/signalREvents';

const HUB_URL = `${API_BASE}/hubs/content-approval`;

// Delays between connection attempts, after a drop and while the first connect keeps failing: quick at first,
// then every 30s for as long as the user is signed in. The SDK default (0, 2, 10, 30s, then give up) left the CMS
// without live updates for good after an API restart longer than ~40s.
const RETRY_DELAYS_MS = [0, 2000, 5000, 10000];
const RETRY_CAP_MS = 30000;
const JITTER_MS = 1000; // spreads reconnects so every open CMS tab does not hit a restarted API in the same second

// A tab hidden at least this long may have missed pushes (browsers throttle hidden tabs), so refetch when it returns.
export const RESYNC_AFTER_HIDDEN_MS = 5 * 60 * 1000;

const { HubConnectionState } = signalR;
const RUNNING_STATES = [HubConnectionState.Connected, HubConnectionState.Connecting, HubConnectionState.Reconnecting];

const retryDelayMs = (attempt) => {
  const base = attempt < RETRY_DELAYS_MS.length ? RETRY_DELAYS_MS[attempt] : RETRY_CAP_MS;
  return base === 0 ? 0 : base + Math.floor(Math.random() * JITTER_MS);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// One connection per login (the hub groups a connection by person, not by space). All state lives here so the
// reconnect logic and the space filter work the same for every page that listens.
let connection = null;
let wantConnected = false;        // true between start() and stop(); every retry loop checks it
let startPromise = null;          // the connect-with-retry loop that is currently running
let stopPromise = null;           // a stop() still in flight; start() waits for it
let hasConnectedBefore = false;   // a later (re)connect has to resync, the very first one does not
let hiddenAt = null;
let browserListenersAttached = false;
const listeners = new Map();      // event -> Map(fn -> wrapped handler)

// A message about a space other than the one the user is working in is dropped. Messages without a spaceId
// (ForceLogout, or an older server) always pass.
const isForAnotherSpace = (payload) => {
  const eventSpaceId = payload?.spaceId;
  if (eventSpaceId == null) return false;
  const activeSpaceId = getSpaceId();
  return activeSpaceId != null && Number(eventSpaceId) !== activeSpaceId;
};

const wrap = (fn, includeOtherSpaces) =>
  includeOtherSpaces
    ? fn
    : (...args) => {
        if (!isForAnotherSpace(args[0])) fn(...args);
      };

const dispatchResync = () => window.dispatchEvent(new Event(SIGNALR_RESYNC_EVENT));

const buildConnection = () => {
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, {
      // Read on every (re)connect, so a token refreshed meanwhile is the one that is sent.
      accessTokenFactory: () => localStorage.getItem(TIFOXR_TOKEN_KEY) || '',
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => (wantConnected ? retryDelayMs(retryContext.previousRetryCount) : null),
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  // Registered before start(): the SDK allows it, and a connection that is rebuilt later gets them again.
  listeners.forEach((byFn, event) => byFn.forEach((handler) => conn.on(event, handler)));

  conn.onreconnected(() => dispatchResync()); // pushes sent while the connection was down are gone
  conn.onclose(() => {
    // Only reached when the SDK gives up on its own (e.g. the server closed it for good). A stop() clears
    // `connection` first, so this never fights a deliberate stop.
    if (wantConnected && connection === conn) {
      connection = null;
      setTimeout(() => ensureStarted(), retryDelayMs(1));
    }
  });

  return conn;
};

async function connectWithRetry(conn) {
  let failures = 0;
  while (wantConnected && connection === conn) {
    try {
      await conn.start();
      if (failures > 0 || hasConnectedBefore) dispatchResync();
      hasConnectedBefore = true;
      return;
    } catch (error) {
      if (!wantConnected || connection !== conn) return; // stopped meanwhile: the failure is expected
      console.warn('SignalR connect failed, retrying:', error?.message || error);
      failures += 1;
      await sleep(retryDelayMs(failures));
    }
  }
}

function ensureStarted() {
  if (!wantConnected) return Promise.resolve();
  if (startPromise) return startPromise;
  if (connection && RUNNING_STATES.includes(connection.state)) return Promise.resolve();

  const conn = buildConnection();
  connection = conn;
  const run = connectWithRetry(conn).finally(() => {
    if (startPromise === run) startPromise = null;
  });
  startPromise = run;
  return run;
}

const isDisconnected = () => !connection || connection.state === HubConnectionState.Disconnected;

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  const hiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
  hiddenAt = null;
  if (!wantConnected) return;

  if (isDisconnected()) ensureStarted();           // dropped while away (sleep, outage): reconnect + resync
  else if (hiddenFor >= RESYNC_AFTER_HIDDEN_MS) dispatchResync();
};

const onOnline = () => {
  if (wantConnected && isDisconnected()) ensureStarted();
};

const attachBrowserListeners = () => {
  if (browserListenersAttached) return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', onOnline);
  browserListenersAttached = true;
};

const detachBrowserListeners = () => {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('online', onOnline);
  browserListenersAttached = false;
  hiddenAt = null;
};

export const signalRService = {
  // Resolves once connected (or once stop() was called); never rejects — it keeps retrying while the user is signed in.
  start: () => {
    wantConnected = true;
    attachBrowserListeners();
    return (stopPromise || Promise.resolve()).then(() => ensureStarted());
  },

  stop: () => {
    wantConnected = false;
    hasConnectedBefore = false;
    detachBrowserListeners();

    // Cleared before awaiting anything: a start() right after this builds a fresh connection that this stop() must
    // never touch, and the retry loop of the old one sees `connection !== conn` and ends.
    const conn = connection;
    connection = null;
    startPromise = null;
    if (!conn) return stopPromise || Promise.resolve();

    const stopping = conn.stop().catch(() => {}).finally(() => {
      if (stopPromise === stopping) stopPromise = null;
    });
    stopPromise = stopping;
    return stopping;
  },

  // `includeOtherSpaces: true` also delivers messages about other spaces (ApprovalContext keeps the user's own
  // outcomes for those until they switch space). Every other listener only sees the active space.
  on: (event, fn, { includeOtherSpaces = false } = {}) => {
    let byFn = listeners.get(event);
    if (!byFn) {
      byFn = new Map();
      listeners.set(event, byFn);
    }
    if (byFn.has(fn)) return;

    const handler = wrap(fn, includeOtherSpaces);
    byFn.set(fn, handler);
    connection?.on(event, handler);
  },

  off: (event, fn) => {
    const byFn = listeners.get(event);
    const handler = byFn?.get(fn);
    if (!handler) return;
    byFn.delete(fn);
    connection?.off(event, handler);
  },
};
